import {
  CalendarIcon,
  ChatAlt2Icon,
  DocumentTextIcon,
  GlobeAltIcon,
  MicrophoneIcon,
  PhoneIcon,
  SearchIcon,
  SparklesIcon,
} from '@heroicons/react/outline';
import type { ComponentType, ReactNode } from 'react';
import { FaGoogleDrive } from 'react-icons/fa6';

import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import { resolveChannelIcon } from '@/components/bots/Integration/ChannelLogo';
import ModelLogo from '@/components/bots/ModelLogo';

import { usd } from './kit';
import type { OurFacts, VisualKey } from './types';
import { CheckGlyph, delay, tone } from '../primitives';

import AnthropicSvg from '~/svg/Anthropic.svg';
import DeepseekSvg from '~/svg/deepseek.svg';
import GoogleSvg from '~/svg/google.svg';
import MiniMaxSvg from '~/svg/Hailuo.svg';
import MetaSvg from '~/svg/meta.svg';
import MoonshotSvg from '~/svg/moonshot.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import XAISvg from '~/svg/xai.svg';
import ZAISvg from '~/svg/zai.svg';

/*
 * Product mock-ups for the "what's different" rows. They show our product
 * only (never a competitor's UI), with a made-up shop, Tidewell, whose colour
 * is deliberately not ours. Phone numbers use the 555-01xx range.
 */

const BRAND = 'bg-[#0f766e]';
const BRAND_NAME = 'Tidewell';
const BRAND_SITE = 'tidewell.co';

const card =
  'rounded-2xl shadow-[0_24px_60px_-30px_rgba(30,20,90,0.35)] ring-1 ring-[#0f0f28]/[0.07] dark:ring-white/[0.08]';

const MAKER_LOGOS: Record<string, ComponentType<{ className?: string }>> = {
  OpenAI: OpenAISvg,
  Anthropic: AnthropicSvg,
  Google: GoogleSvg,
  xAI: XAISvg,
  DeepSeek: DeepseekSvg,
  Meta: MetaSvg,
  Moonshot: MoonshotSvg,
  'Z.ai': ZAISvg,
  MiniMax: MiniMaxSvg,
};

const plain = (value: number) => new Intl.NumberFormat('en-US').format(value);

export function Visual({ kind, ours }: { kind: VisualKey; ours: OurFacts }) {
  switch (kind) {
    case 'agents':
      return <AgentsVisual ours={ours} />;
    case 'branding':
      return <BrandingVisual />;
    case 'models':
      return <ModelsVisual ours={ours} />;
    case 'usage':
      return <UsageVisual ours={ours} />;
    case 'channels':
      return <ChannelsVisual />;
    case 'voice':
      return <VoiceVisual />;
    case 'actions':
      return <ActionsVisual />;
    case 'knowledge':
      return <KnowledgeVisual />;
    case 'builder':
      return <BuilderVisual />;
    case 'nightShift':
      return <NightShiftVisual />;
    case 'flatPrice':
      return <FlatPriceVisual ours={ours} />;
    case 'leads':
      return <LeadsVisual />;
  }
}

function Pill({
  children,
  tint = 'indigo',
}: {
  children: ReactNode;
  tint?: 'indigo' | 'emerald' | 'amber' | 'slate';
}) {
  return (
    <span
      className={clsxm(
        'inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tint === 'indigo' &&
          'bg-primary-50 text-primary-700 dark:bg-primary-400/10 dark:text-primary-300',
        tint === 'emerald' &&
          'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300',
        tint === 'amber' &&
          'bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300',
        tint === 'slate' &&
          'bg-[#f1f1f5] text-[#56566d] dark:bg-white/[0.07] dark:text-[#a4a4bd]',
      )}
    >
      {children}
    </span>
  );
}

/** A floating confirmation chip that overlaps the card above it. */
function FloatChip({
  children,
  className,
  at = 260,
}: {
  children: ReactNode;
  className?: string;
  at?: number;
}) {
  return (
    <div
      className={clsxm(
        styles.rv,
        'relative z-10 w-fit rounded-full bg-white px-4 py-2.5 text-sm font-medium shadow-[0_18px_40px_-18px_rgba(30,20,90,0.5)] ring-1 ring-[#0f0f28]/[0.07] dark:bg-[#1c1c2b] dark:ring-white/10',
        tone.ink,
        className,
      )}
      style={delay(at)}
    >
      <span className='flex items-center gap-2'>
        <span className='flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
          <CheckGlyph className='h-3 w-3' />
        </span>
        {children}
      </span>
    </div>
  );
}

function ModelsVisual({ ours }: { ours: OurFacts }) {
  return (
    <div className='relative'>
      <div
        className={clsxm(styles.rv, card, tone.surface, 'p-4 sm:p-5')}
        style={delay(0)}
      >
        <div className='flex items-center justify-between gap-3'>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
            Choose a model
          </p>
          <span className={clsxm('text-xs tabular-nums', tone.quiet)}>
            {ours.modelCount} models · {ours.makers.length} AI labs
          </span>
        </div>
        <div
          className={clsxm(
            'mt-3 flex items-center gap-2 rounded-xl bg-[#f7f7fa] px-3 py-2 text-sm dark:bg-white/[0.04]',
            tone.quiet,
          )}
        >
          <SearchIcon className='h-4 w-4' />
          Search models
        </div>
        <ul className='mt-3 space-y-1.5'>
          {ours.showcase.map((model, index) => {
            const Logo = MAKER_LOGOS[model.maker];
            return (
              <li
                key={model.title}
                className={clsxm(
                  'flex items-center gap-3 rounded-xl px-3 py-2.5',
                  index === 1
                    ? 'bg-primary-50 ring-1 ring-primary-200 dark:bg-primary-400/10 dark:ring-primary-400/30'
                    : 'bg-[#f7f7fa] dark:bg-white/[0.04]',
                )}
              >
                {Logo && <ModelLogo icon={Logo} className='h-5 w-5' />}
                <span className='min-w-0 flex-1'>
                  <span
                    className={clsxm(
                      'block truncate text-sm font-semibold',
                      tone.ink,
                    )}
                  >
                    {model.title}
                  </span>
                  <span className={clsxm('block truncate text-xs', tone.quiet)}>
                    {model.maker}
                  </span>
                </span>
                {model.isDefault && <Pill>Default</Pill>}
                {model.unmetered && (
                  <Pill tint='emerald'>Doesn’t use words</Pill>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {ours.showcase[1] && (
        <FloatChip className='-mt-5 me-2 ms-auto sm:me-6'>
          Switched to {ours.showcase[1].title}. No retraining.
        </FloatChip>
      )}
    </div>
  );
}

function UsageVisual({ ours }: { ours: OurFacts }) {
  const plan =
    ours.plans.find((item) => item.name === 'Unlimited') ?? ours.plans[2];
  const words = plan?.words ?? 100000;
  const used = Math.round(words * 0.42);

  return (
    <div className='relative'>
      <div
        className={clsxm(styles.rv, card, tone.surface, 'p-5 sm:p-6')}
        style={delay(0)}
      >
        <div className='flex items-center justify-between gap-3'>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
            Usage this month
          </p>
          <Pill tint='slate'>{plan?.name ?? 'Unlimited'} plan</Pill>
        </div>

        <div className='mt-5 grid grid-cols-2 gap-3'>
          <div className='rounded-xl bg-[#f7f7fa] p-4 dark:bg-white/[0.04]'>
            <p className={clsxm('text-xs', tone.quiet)}>Agent messages</p>
            <p
              className={clsxm(
                'mt-1 text-2xl font-semibold tracking-tight',
                tone.ink,
              )}
            >
              No cap
            </p>
          </div>
          <div className='rounded-xl bg-[#f7f7fa] p-4 dark:bg-white/[0.04]'>
            <p className={clsxm('text-xs', tone.quiet)}>Per-message fees</p>
            <p
              className={clsxm(
                'mt-1 text-2xl font-semibold tracking-tight',
                tone.ink,
              )}
            >
              $0
            </p>
          </div>
        </div>

        <div className='mt-5'>
          <div className='flex justify-between gap-3 text-xs'>
            <span className={tone.body}>Words used by premium models</span>
            <span className={clsxm('tabular-nums', tone.quiet)}>
              {plain(used)} / {plain(words)}
            </span>
          </div>
          <div className='mt-2 h-1.5 overflow-hidden rounded-full bg-[#ececf3] dark:bg-white/10'>
            <div className='h-full w-[42%] rounded-full bg-primary-600' />
          </div>
        </div>

        <p className={clsxm('mt-5 text-xs font-medium', tone.quiet)}>
          Models that don’t use words
        </p>
        <div className='mt-2 flex flex-wrap gap-1.5'>
          {ours.unmetered.slice(0, 4).map((title) => (
            <Pill key={title} tint='emerald'>
              {title}
            </Pill>
          ))}
        </div>
      </div>
    </div>
  );
}

const CHANNELS: { name: string; logo: string; on?: boolean }[] = [
  { name: 'Website', logo: 'web', on: true },
  { name: 'WhatsApp', logo: 'FaWhatsapp', on: true },
  { name: 'Messenger', logo: 'FaFacebookMessenger' },
  { name: 'Telegram', logo: 'FaTelegram' },
  { name: 'LINE', logo: 'FaLine' },
  { name: 'Slack', logo: 'FaSlack' },
  { name: 'Gmail', logo: 'Gmail', on: true },
  { name: 'Outlook', logo: 'Outlook' },
  { name: 'Phone calls', logo: 'FaPhone' },
  { name: 'Shopify', logo: 'FaShopify' },
  { name: 'WordPress', logo: 'FaWordpress' },
  { name: 'Zapier', logo: 'Zapier' },
];

function ChannelsVisual() {
  return (
    <div
      className={clsxm(styles.rv, card, tone.surface, 'p-4 sm:p-5')}
      style={delay(0)}
    >
      <div className='flex items-center justify-between gap-3'>
        <p className={clsxm('text-[15px] font-semibold', tone.ink)}>Channels</p>
        <span className={clsxm('text-xs', tone.quiet)}>3 connected</span>
      </div>
      <ul className='mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3'>
        {CHANNELS.map((channel, index) => (
          <li
            key={channel.name}
            className={clsxm(
              styles.rv,
              'flex min-w-0 items-center gap-2.5 rounded-xl bg-[#f7f7fa] px-3 py-2.5 dark:bg-white/[0.04]',
            )}
            style={delay(60 + index * 35)}
          >
            <span className='flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-white ring-1 ring-[#0f0f28]/[0.06] dark:ring-white/10'>
              {channel.logo === 'web' ? (
                <ChatAlt2Icon className='h-4 w-4 text-primary-600 dark:text-primary-400' />
              ) : (
                resolveChannelIcon(channel.logo, 'h-4 w-4')
              )}
            </span>
            <span className='min-w-0 flex-1'>
              <span
                className={clsxm(
                  'block truncate text-[13px] font-semibold',
                  tone.ink,
                )}
              >
                {channel.name}
              </span>
              <span
                className={clsxm(
                  'block text-[11px]',
                  channel.on
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : tone.quiet,
                )}
              >
                {channel.on ? 'Connected' : 'Connect'}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VoiceVisual() {
  return (
    <div className='relative'>
      <div
        className={clsxm(
          styles.rv,
          card,
          'overflow-hidden bg-[#0d0c1f] p-5 text-white sm:p-6',
        )}
        style={delay(0)}
      >
        <div className='flex items-center justify-between text-xs text-white/60'>
          <span className='flex items-center gap-2'>
            <span className='h-1.5 w-1.5 rounded-full bg-emerald-400' />
            {BRAND_NAME} · voice call
          </span>
          <span className='tabular-nums'>0:42</span>
        </div>
        <div className='relative mx-auto mt-6 flex h-28 w-28 items-center justify-center'>
          <span className='absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(129,140,248,0.55)_0%,rgba(129,140,248,0)_70%)]' />
          <span
            className={clsxm(
              styles.wave,
              styles.waveCenter,
              'relative flex h-16 w-16 items-center justify-center gap-1 rounded-full',
              BRAND,
            )}
          >
            {[0, 120, 240, 360, 480].map((ms) => (
              <span
                key={ms}
                className='h-6 w-1 rounded-full bg-white/90'
                style={delay(ms)}
              />
            ))}
          </span>
        </div>
        <p className='mx-auto mt-5 max-w-xs text-center text-sm leading-6 text-white/85'>
          “Yes, we deliver on Saturdays. It’s free on orders over $50.”
        </p>
        <div className='mt-5 flex items-center justify-center gap-2'>
          <span className='inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3.5 py-2 text-xs font-semibold'>
            <MicrophoneIcon className='h-3.5 w-3.5' />
            Mute
          </span>
          <span className='inline-flex items-center rounded-full bg-[#e5484d] px-4 py-2 text-xs font-semibold'>
            End
          </span>
        </div>
      </div>
      <div
        className={clsxm(
          styles.rv,
          card,
          tone.surface,
          'relative z-10 -mt-6 me-auto ms-4 flex w-[88%] items-center gap-3 p-4 sm:ms-8 sm:w-[70%]',
        )}
        style={delay(220)}
      >
        <span className='flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#F22F46]/10 text-[#F22F46]'>
          <PhoneIcon className='h-5 w-5' />
        </span>
        <span className='min-w-0'>
          <span className={clsxm('block text-sm font-semibold', tone.ink)}>
            +1 (555) 010-0142
          </span>
          <span className={clsxm('block truncate text-xs', tone.quiet)}>
            Phone call answered by your agent · 1:12
          </span>
        </span>
      </div>
    </div>
  );
}

function ChatHeader({ status }: { status?: ReactNode }) {
  return (
    <div
      className={clsxm('flex items-center gap-3 px-4 py-3 text-white', BRAND)}
    >
      <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white/20 text-sm font-bold'>
        T
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-sm font-semibold'>
          {BRAND_NAME}
        </span>
        <span className='block truncate text-[11px] text-white/75'>
          {status ?? 'AI agent · replies instantly'}
        </span>
      </span>
    </div>
  );
}

function Visitor({ children }: { children: ReactNode }) {
  return (
    <div className='flex justify-end'>
      <p
        className={clsxm(
          'max-w-[82%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-3.5 py-2 text-sm dark:bg-white/[0.07]',
          tone.ink,
        )}
      >
        {children}
      </p>
    </div>
  );
}

function Agent({ children }: { children: ReactNode }) {
  return (
    <div
      className={clsxm(
        'max-w-[88%] rounded-2xl rounded-es-md px-3.5 py-2 text-sm leading-relaxed text-white',
        BRAND,
      )}
    >
      {children}
    </div>
  );
}

function ActionsVisual() {
  return (
    <div className='relative'>
      <div
        className={clsxm(styles.rv, card, tone.surface, 'overflow-hidden')}
        style={delay(0)}
      >
        <ChatHeader />
        <div className='space-y-3 p-4'>
          <Visitor>Can I book a design consultation on Thursday?</Visitor>
          <Agent>
            Of course. These times are free on Thursday:
            <span className='mt-2.5 flex flex-wrap gap-1.5'>
              {['10:00', '13:30', '16:00'].map((time, index) => (
                <span
                  key={time}
                  className={clsxm(
                    'rounded-full px-3 py-1 text-xs font-semibold',
                    index === 1
                      ? 'bg-white text-[#0f766e]'
                      : 'bg-white/20 text-white',
                  )}
                >
                  {time}
                </span>
              ))}
            </span>
          </Agent>
          <Visitor>13:30 please. I’m Maya, maya@example.com</Visitor>
          <p className='flex items-center gap-2 ps-1 text-xs font-medium text-emerald-700 dark:text-emerald-400'>
            <CalendarIcon className='h-4 w-4' />
            Booked in Google Calendar · Thursday 13:30
          </p>
        </div>
      </div>
      <div
        className={clsxm(
          styles.rv,
          card,
          tone.surface,
          'relative z-10 -mt-4 me-3 ms-auto w-[86%] p-4 sm:w-[64%]',
        )}
        style={delay(240)}
      >
        <p
          className={clsxm(
            'text-[11px] font-semibold uppercase tracking-[0.08em]',
            tone.quiet,
          )}
        >
          Behind the scenes
        </p>
        <ul className='mt-2.5 space-y-2 text-[13px]'>
          {[
            ['Google Calendar', 'found 3 free times'],
            ['Collect Data', 'saved name and email'],
          ].map(([action, detail]) => (
            <li key={action} className='flex items-center gap-2'>
              <SparklesIcon className='h-4 w-4 flex-none text-primary-600 dark:text-primary-400' />
              <span className={clsxm('font-semibold', tone.ink)}>{action}</span>
              <span className={clsxm('truncate', tone.quiet)}>{detail}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function KnowledgeVisual() {
  const rows: {
    icon: ReactNode;
    name: string;
    kind: string;
    status: 'done' | 'training';
  }[] = [
    {
      icon: <GlobeAltIcon className={clsxm('h-4 w-4', tone.quiet)} />,
      name: BRAND_SITE,
      kind: '48 pages',
      status: 'done',
    },
    {
      icon: <FaGoogleDrive className='h-4 w-4 text-[#1FA463]' />,
      name: 'Product care guide',
      kind: 'Google Drive · synced',
      status: 'done',
    },
    {
      icon: <DocumentTextIcon className={clsxm('h-4 w-4', tone.quiet)} />,
      name: 'Returns policy.pdf',
      kind: 'PDF',
      status: 'done',
    },
    {
      icon: <ChatAlt2Icon className={clsxm('h-4 w-4', tone.quiet)} />,
      name: 'Do you ship to Canada?',
      kind: 'Q&A',
      status: 'training',
    },
  ];

  return (
    <div
      className={clsxm(styles.rv, card, tone.surface, 'p-5 sm:p-6')}
      style={delay(0)}
    >
      <div className='flex items-center justify-between gap-3'>
        <div>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
            Knowledge
          </p>
          <p className={clsxm('text-xs', tone.quiet)}>
            4 sources · Drive files re-checked every 6 hours
          </p>
        </div>
        <Pill tint='slate'>Retrain</Pill>
      </div>
      <ul className='mt-5 space-y-2'>
        {rows.map((row, index) => (
          <li
            key={row.name}
            className={clsxm(
              styles.rv,
              'flex items-center gap-3 rounded-xl bg-[#f7f7fa] px-3.5 py-2.5 dark:bg-white/[0.04]',
            )}
            style={delay(80 + index * 60)}
          >
            <span className='flex-none'>{row.icon}</span>
            <span
              className={clsxm(
                'min-w-0 flex-1 truncate text-sm font-medium',
                tone.ink,
              )}
            >
              {row.name}
            </span>
            <span className={clsxm('hidden text-xs sm:inline', tone.quiet)}>
              {row.kind}
            </span>
            {row.status === 'done' ? (
              <Pill tint='emerald'>Trained</Pill>
            ) : (
              <Pill tint='amber'>Training</Pill>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BuilderVisual() {
  const steps = [
    'Read 48 pages of tidewell.co',
    'Wrote its instructions',
    'Matched your brand colors',
    'Ready to test in the Playground',
  ];
  return (
    <div className='relative'>
      <div
        className={clsxm(styles.rv, card, tone.surface, 'p-5 sm:p-6')}
        style={delay(0)}
      >
        <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
          Create an agent
        </p>
        <div className='mt-4 flex items-center gap-2 rounded-xl bg-[#f7f7fa] p-1.5 ps-3.5 dark:bg-white/[0.04]'>
          <GlobeAltIcon className={clsxm('h-4 w-4 flex-none', tone.quiet)} />
          <span className={clsxm('min-w-0 flex-1 truncate text-sm', tone.ink)}>
            {BRAND_SITE}
          </span>
          <span className='rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white'>
            Build
          </span>
        </div>
        <ol className='mt-5 space-y-3'>
          {steps.map((step, index) => (
            <li
              key={step}
              className={clsxm(styles.rv, 'flex items-center gap-3 text-sm')}
              style={delay(120 + index * 110)}
            >
              <span className='flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
                <CheckGlyph className='h-3.5 w-3.5' />
              </span>
              <span className={tone.ink}>{step}</span>
            </li>
          ))}
        </ol>
      </div>
      <FloatChip className='-mt-5 me-4 ms-auto' at={600}>
        No flows to draw, no code to write
      </FloatChip>
    </div>
  );
}

function NightShiftVisual() {
  return (
    <div className='relative'>
      <div
        className={clsxm(styles.rv, card, tone.surface, 'overflow-hidden')}
        style={delay(0)}
      >
        <ChatHeader status='AI agent · your team is offline' />
        <div className='space-y-3 p-4'>
          <p className={clsxm('text-center text-[11px]', tone.quiet)}>
            Today, 2:14 a.m.
          </p>
          <Visitor>Is the oak dining table still in stock?</Visitor>
          <Agent>
            Yes, 3 are left in natural oak, and delivery takes 5 to 7 days.
            Shall I send the link to your email?
          </Agent>
          <Visitor>Yes please, sam@example.com</Visitor>
          <Agent>Done! The link is on its way.</Agent>
        </div>
      </div>
      <FloatChip className='-mt-5 me-4 ms-auto' at={300}>
        Answered at 2:14 a.m. · lead saved
      </FloatChip>
    </div>
  );
}

function FlatPriceVisual({ ours }: { ours: OurFacts }) {
  const plan = ours.plans.find((item) => item.name === 'Unlimited');
  const price = plan?.monthly;
  const model = ours.unmetered[0];
  const rows: [string, string][] = [
    ['Conversations this month', '2,431'],
    ['Model', model ? `${model} (doesn’t use words)` : 'Any model'],
    ['Per-conversation fees', '$0.00'],
  ];

  return (
    <div
      className={clsxm(styles.rv, card, tone.surface, 'p-5 sm:p-6')}
      style={delay(0)}
    >
      <div className='flex items-start justify-between gap-4'>
        <div>
          <p className={clsxm('text-xs', tone.quiet)}>Your plan</p>
          <p className={clsxm('mt-0.5 text-[15px] font-semibold', tone.ink)}>
            Easy-Peasy.AI {plan?.name ?? 'Unlimited'}
          </p>
        </div>
        <Pill tint='emerald'>Flat monthly price</Pill>
      </div>
      <ul className='mt-5 divide-y divide-[#0f0f28]/[0.06] dark:divide-white/[0.07]'>
        {rows.map(([label, value]) => (
          <li
            key={label}
            className='flex items-center justify-between gap-4 py-3 text-sm'
          >
            <span className={tone.body}>{label}</span>
            <span
              className={clsxm('text-end font-medium tabular-nums', tone.ink)}
            >
              {value}
            </span>
          </li>
        ))}
      </ul>
      <div className='mt-2 flex items-end justify-between gap-4 border-t border-[#0f0f28]/[0.1] pt-4 dark:border-white/[0.1]'>
        <span className={clsxm('text-sm font-semibold', tone.ink)}>
          This month
        </span>
        <span
          className={clsxm(
            'text-3xl font-semibold tracking-tight tabular-nums',
            tone.ink,
          )}
        >
          {price ? usd(price) : 'Flat'}
        </span>
      </div>
    </div>
  );
}

function LeadsVisual() {
  const leads: [string, string, string, string][] = [
    ['Maya Chen', 'maya@example.com', 'web', '2 min ago'],
    ['Sam Ortiz', 'sam@example.com', 'FaWhatsapp', '1 h ago'],
    ['Priya Nair', 'priya@example.com', 'web', '3 h ago'],
    ['Leo Brandt', 'leo@example.com', 'FaTelegram', 'Yesterday'],
  ];

  return (
    <div className='relative'>
      <div
        className={clsxm(styles.rv, card, tone.surface, 'p-4 sm:p-5')}
        style={delay(0)}
      >
        <div className='flex items-center justify-between gap-3'>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>Leads</p>
          <Pill tint='indigo'>4 new this week</Pill>
        </div>
        <ul className='mt-4 space-y-1.5'>
          {leads.map(([name, email, channel, when], index) => (
            <li
              key={email}
              className={clsxm(
                styles.rv,
                'flex items-center gap-3 rounded-xl bg-[#f7f7fa] px-3 py-2.5 dark:bg-white/[0.04]',
              )}
              style={delay(60 + index * 70)}
            >
              <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700 dark:bg-primary-400/15 dark:text-primary-300'>
                {name
                  .split(' ')
                  .map((part) => part[0])
                  .join('')}
              </span>
              <span className='min-w-0 flex-1'>
                <span
                  className={clsxm(
                    'block truncate text-sm font-semibold',
                    tone.ink,
                  )}
                >
                  {name}
                </span>
                <span className={clsxm('block truncate text-xs', tone.quiet)}>
                  {email}
                </span>
              </span>
              <span className='flex-none'>
                {channel === 'web' ? (
                  <ChatAlt2Icon className='h-4 w-4 text-primary-600 dark:text-primary-400' />
                ) : (
                  resolveChannelIcon(channel, 'h-4 w-4')
                )}
              </span>
              <span
                className={clsxm(
                  'hidden w-16 flex-none text-end text-xs sm:inline',
                  tone.quiet,
                )}
              >
                {when}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <FloatChip className='-mt-5 me-4 ms-auto' at={420}>
        New lead emailed to your team
      </FloatChip>
    </div>
  );
}

function AgentsVisual({ ours }: { ours: OurFacts }) {
  const plan =
    ours.plans.find((item) => item.name === 'Unlimited') ?? ours.plans[2];
  const allowed = plan?.agents ?? 4;
  const agents: {
    name: string;
    tint: string;
    model: string;
    channels: string[];
  }[] = [
    {
      name: 'Tidewell Support',
      tint: BRAND,
      model: ours.showcase[0]?.title ?? 'GPT-6 Luna',
      channels: ['web', 'FaWhatsapp', 'Gmail'],
    },
    {
      name: 'Tidewell Sales',
      tint: 'bg-[#b45309]',
      model: ours.showcase[1]?.title ?? 'Claude',
      channels: ['web', 'FaPhone'],
    },
    {
      name: 'Tidewell Español',
      tint: 'bg-[#7c3aed]',
      model: ours.showcase[2]?.title ?? 'Gemini',
      channels: ['FaWhatsapp', 'FaTelegram'],
    },
  ];

  return (
    <div className='relative'>
      <div
        className={clsxm(styles.rv, card, tone.surface, 'p-4 sm:p-5')}
        style={delay(0)}
      >
        <div className='flex items-center justify-between gap-3'>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>Agents</p>
          <span className={clsxm('text-xs tabular-nums', tone.quiet)}>
            {agents.length} / {allowed} on your plan
          </span>
        </div>
        <div className='mt-3 h-1.5 overflow-hidden rounded-full bg-[#ececf3] dark:bg-white/10'>
          <div
            className='h-full rounded-full bg-primary-600'
            style={{ width: `${Math.round((agents.length / allowed) * 100)}%` }}
          />
        </div>
        <ul className='mt-4 space-y-2'>
          {agents.map((agent, index) => (
            <li
              key={agent.name}
              className={clsxm(
                styles.rv,
                'flex items-center gap-3 rounded-xl bg-[#f7f7fa] px-3 py-2.5 dark:bg-white/[0.04]',
              )}
              style={delay(80 + index * 90)}
            >
              <span
                className={clsxm(
                  'flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-bold text-white',
                  agent.tint,
                )}
              >
                {agent.name.split(' ')[1]?.slice(0, 2) ?? 'T'}
              </span>
              <span className='min-w-0 flex-1'>
                <span
                  className={clsxm(
                    'block truncate text-sm font-semibold',
                    tone.ink,
                  )}
                >
                  {agent.name}
                </span>
                <span className={clsxm('block truncate text-xs', tone.quiet)}>
                  {agent.model}
                </span>
              </span>
              <span className='flex flex-none items-center gap-1.5'>
                {agent.channels.map((channel) => (
                  <span key={channel} className='flex h-4 w-4 items-center'>
                    {channel === 'web' ? (
                      <ChatAlt2Icon className='h-4 w-4 text-primary-600 dark:text-primary-400' />
                    ) : (
                      resolveChannelIcon(channel, 'h-4 w-4')
                    )}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <FloatChip className='-mt-5 me-4 ms-auto' at={420}>
        New agent added. No extra charge.
      </FloatChip>
    </div>
  );
}

function BrandingVisual() {
  return (
    <div className='relative'>
      <div
        className={clsxm(
          styles.rv,
          card,
          tone.surface,
          'mx-auto max-w-[22rem] overflow-hidden',
        )}
        style={delay(0)}
      >
        <ChatHeader status='Usually replies in seconds' />
        <div className='space-y-3 p-4'>
          <Agent>
            Hi! I’m Tidewell’s assistant. Ask me about orders, delivery or our
            products.
          </Agent>
          <Visitor>Do you have the linen throw in sage?</Visitor>
          <Agent>Yes, it’s in stock in sage and oat, $68.</Agent>
        </div>
        <div className='border-t border-[#0f0f28]/[0.06] px-4 py-3 dark:border-white/[0.07]'>
          <div
            className={clsxm(
              'rounded-full bg-[#f7f7fa] px-4 py-2.5 text-sm dark:bg-white/[0.04]',
              tone.quiet,
            )}
          >
            Message Tidewell…
          </div>
          <p className={clsxm('mt-2 text-center text-[11px]', tone.quiet)}>
            Tidewell · Privacy policy
          </p>
        </div>
      </div>
      <div
        className={clsxm(
          styles.rv,
          card,
          tone.surface,
          'relative z-10 -mt-10 me-auto ms-0 flex w-[92%] items-center gap-3 p-4 sm:-ms-2 sm:w-[78%]',
        )}
        style={delay(240)}
      >
        <span className='min-w-0 flex-1'>
          <span className={clsxm('block text-sm font-semibold', tone.ink)}>
            Show “Powered by” badge
          </span>
          <span className={clsxm('block text-xs', tone.quiet)}>
            Included from Unlimited 200
          </span>
        </span>
        <span
          role='presentation'
          className='relative h-6 w-11 flex-none rounded-full bg-[#d9d9e3] dark:bg-white/15'
        >
          <span className='absolute start-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow' />
        </span>
      </div>
    </div>
  );
}
